import { useEffect, useRef, useState, type HTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { ParticipantRole } from '../store/registrationsSlice'
import { useAnchoredMenuPosition } from '../hooks/useAnchoredMenuPosition'
import { Button } from './Button'
import { ROLE_STYLES } from './roleStyles'

// ── Style map ─────────────────────────────────────────────────────────────────

// "Staff" is hidden from the frontend picker (backend/data model still support it for any
// existing participants) — only these three roles can be newly assigned via the UI.
const SELECTABLE_ROLES: ParticipantRole[] = ['Audience', 'Speaker', 'Host']

// ── Read-only badge ───────────────────────────────────────────────────────────

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  role: ParticipantRole
  className?: string
  /** Larger variant used where the badge needs to stand out (e.g. the scanner kiosk). */
  size?: 'sm' | 'lg'
}

/**
 * Static read-only pill for displaying a participant role.
 * Used on the ticket page and anywhere a non-interactive badge is needed.
 */
export function RoleBadge({ role, className = '', size = 'sm', ...rest }: BadgeProps) {
  const { t } = useTranslation()
  const sizeClasses =
    size === 'lg'
      ? 'px-3.5 py-1 text-sm font-bold tracking-wide'
      : 'px-2.5 py-0.5 text-[10px] font-semibold tracking-wide'
  return (
    <span
      className={`inline-flex items-center rounded-full uppercase ${sizeClasses} ${ROLE_STYLES[role]} ${className}`}
      {...rest}
    >
      {t(`role_${role.toLowerCase()}`)}
    </span>
  )
}

// ── Chevron icon ──────────────────────────────────────────────────────────────

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={`pointer-events-none shrink-0 opacity-60 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

// ── Inline select ─────────────────────────────────────────────────────────────

interface SelectProps {
  role: ParticipantRole
  registrationId: string
  onChange: (id: string, role: ParticipantRole) => void
}

/**
 * Badge-styled dropdown for assigning a role inline inside the accepted-participants table.
 * Fully custom-rendered (rather than a native <select>) so its popup panel matches the rest of
 * the app's rounding, font, and shadow conventions — native option lists can't be styled this way.
 */
export function RoleSelect({ role, registrationId, onChange }: SelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  // Every role is offered; if a participant somehow has a role outside the known set, surface it
  // first so the pill still reflects the real value.
  const options = SELECTABLE_ROLES.includes(role) ? SELECTABLE_ROLES : [role, ...SELECTABLE_ROLES]

  // ~36px per option row (padding + pill) + the list's own padding — used only to decide whether
  // the menu fits below the trigger before flipping it above (see useAnchoredMenuPosition).
  const menuPosition = useAnchoredMenuPosition(containerRef, open, 'left', options.length * 36 + 16)

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node
      // The menu is rendered via a portal into `document.body`, so it lives outside
      // `containerRef` — it must be checked separately, otherwise this listener's `mousedown`
      // fires and closes (unmounts) the menu before the option's own `click` handler ever runs.
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  function select(r: ParticipantRole) {
    onChange(registrationId, r)
    setOpen(false)
  }

  return (
    <div className="relative inline-block" ref={containerRef}>
      <Button
        type="button"
        variant="unstyled"
        aria-label="Role for participant"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center justify-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase transition-opacity hover:opacity-80 ${ROLE_STYLES[role]}`}
      >
        {/* Fixed width (matches the dropdown options below) so every role pill — "Speaker",
            "Host", "Audience" — renders at the same size instead of each trigger being only as
            wide as its own label, which made rows with longer role names visually crowd the
            next column. */}
        <span className="w-[52px] text-center">{t(`role_${role.toLowerCase()}`)}</span>
        <ChevronDown open={open} />
      </Button>

      {open &&
        menuPosition &&
        createPortal(
          // Rendered via portal (rather than plain `position: absolute`) so the panel escapes any
          // clipping ancestor — e.g. the accepted-participants table's `overflow-x-auto` wrapper,
          // which would otherwise cut it off or hide it entirely for rows near the table's edges.
          <ul
            ref={menuRef}
            role="listbox"
            aria-label="Role"
            style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left }}
            className="z-50 min-w-[110px] list-none rounded-xl border border-gray-200 bg-white p-1 shadow-lg"
          >
            {options.map((r) => (
              <li key={r}>
                <Button
                  type="button"
                  variant="unstyled"
                  role="option"
                  aria-selected={role === r}
                  onClick={() => select(r)}
                  className="flex w-full items-center justify-center rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-50"
                >
                  <span
                    className={`inline-flex w-[72px] items-center justify-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${ROLE_STYLES[r]}`}
                  >
                    {t(`role_${r.toLowerCase()}`)}
                  </span>
                </Button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  )
}
